/**
 * Brand animation — typed, promise-returning WAAPI timelines over the mark's
 * named parts. The choreography vocabulary:
 *
 *   - `enter`      — the mark composes itself: the stem draws in (clip
 *                    reveal, top→bottom), the bowl swings from its top-
 *                    terminal hinge and settles, the app-name letters appear
 *                    with a stagger, and the dot drops in LAST — the full
 *                    stop that ends the sentence.
 *   - `app-switch` — the current name's letters leave (reverse stagger), the
 *                    name changes, the dot slides to its new home while the
 *                    new letters arrive and the bowl gives a small nod.
 *   - `dot-bounce` — a playful ping on the dot (hover, notification, …).
 *
 * Timing/easing knobs are read from the SAME custom properties the CSS
 * keyframes use (`--brand-enter-duration`, `--brand-letter-stagger`, …), so
 * tuning one set of knobs tunes both the CSS and the WAAPI paths.
 *
 * Every timeline resolves; environments without `Element.animate`
 * (happy-dom, SSR) and `prefers-reduced-motion` short-circuit to the final
 * state immediately. All end states equal the natural resting state, so
 * animations run with `fill: "backwards"` and leave NO residue.
 */

export type BrandAnimationName = "enter" | "app-switch" | "dot-bounce";

/** The mark's animatable parts, queried by their part classes. */
export interface BrandParts {
  /** The element the timing knobs are read from. */
  host: Element;
  stem: Element | null;
  bowl: Element | null;
  dot: Element | null;
  /** Per-letter tspans of the app name, document order. */
  letters: readonly Element[];
}

export interface BrandKnobs {
  enterMs: number;
  switchMs: number;
  staggerMs: number;
  easeOut: string;
  easeSwing: string;
  easeIn: string;
}

const FALLBACK_KNOBS: BrandKnobs = {
  enterMs: 640,
  switchMs: 360,
  staggerMs: 36,
  easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
  easeSwing: "cubic-bezier(0.34, 1.42, 0.44, 1)",
  easeIn: "cubic-bezier(0.55, 0, 0.7, 0.3)",
};

function parseMs(raw: string): number | null {
  const value = raw.trim();
  if (value.endsWith("ms")) {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }
  if (value.endsWith("s")) {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n * 1000 : null;
  }
  return null;
}

/** Read the motion knobs off the host's computed style (CSS custom props). */
export function readKnobs(host: Element): BrandKnobs {
  if (typeof getComputedStyle !== "function") return FALLBACK_KNOBS;
  try {
    const cs = getComputedStyle(host);
    const ms = (prop: string, fallback: number): number =>
      parseMs(cs.getPropertyValue(prop)) ?? fallback;
    const str = (prop: string, fallback: string): string => {
      const v = cs.getPropertyValue(prop).trim();
      return v.length > 0 ? v : fallback;
    };
    return {
      enterMs: ms("--brand-enter-duration", FALLBACK_KNOBS.enterMs),
      switchMs: ms("--brand-switch-duration", FALLBACK_KNOBS.switchMs),
      staggerMs: ms("--brand-letter-stagger", FALLBACK_KNOBS.staggerMs),
      easeOut: str("--brand-ease-out", FALLBACK_KNOBS.easeOut),
      easeSwing: str("--brand-ease-swing", FALLBACK_KNOBS.easeSwing),
      easeIn: str("--brand-ease-in", FALLBACK_KNOBS.easeIn),
    };
  } catch {
    return FALLBACK_KNOBS;
  }
}

/** True when the environment asks us to skip motion. */
export function reducedMotion(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

type Animatable = Element & {
  animate?: (
    keyframes: Keyframe[],
    options: KeyframeAnimationOptions
  ) => Animation;
};

/**
 * One guarded WAAPI run: resolves when finished, resolves immediately where
 * `animate` is missing, swallows cancellation. Collects the live Animation
 * into `bag` so a later timeline can cancel this one.
 */
function run(
  el: Element | null,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
  bag: Animation[]
): Promise<void> {
  const target = el as Animatable | null;
  if (!target || typeof target.animate !== "function") {
    return Promise.resolve();
  }
  try {
    const animation = target.animate(keyframes, {
      fill: "backwards",
      ...options,
    });
    bag.push(animation);
    return animation.finished.then(
      () => undefined,
      () => undefined // cancelled — a newer timeline took over
    );
  } catch {
    return Promise.resolve();
  }
}

/** The `enter` timeline. */
export function playEnter(parts: BrandParts, bag: Animation[]): Promise<void> {
  if (reducedMotion()) return Promise.resolve();
  const k = readKnobs(parts.host);
  const jobs: Promise<void>[] = [];

  jobs.push(
    run(
      parts.stem,
      [
        { clipPath: "inset(-10% -10% 110% -10%)" },
        { clipPath: "inset(-10% -10% -10% -10%)" },
      ],
      { duration: k.enterMs, easing: k.easeOut },
      bag
    )
  );

  jobs.push(
    run(
      parts.bowl,
      [
        { transform: "rotate(-16deg)", opacity: 0, offset: 0 },
        { opacity: 1, offset: 0.45 },
        { transform: "rotate(2.6deg)", offset: 0.7 },
        { transform: "rotate(0deg)", opacity: 1, offset: 1 },
      ],
      { duration: k.enterMs * 1.1, delay: k.enterMs * 0.45, easing: k.easeSwing },
      bag
    )
  );

  parts.letters.forEach((letter, i) => {
    jobs.push(
      run(
        letter,
        [{ fillOpacity: 0 }, { fillOpacity: 1 }],
        {
          duration: k.enterMs * 0.5,
          delay: k.enterMs * 0.7 + i * k.staggerMs,
          easing: k.easeOut,
        },
        bag
      )
    );
  });

  jobs.push(
    run(
      parts.dot,
      [
        { transform: "translateY(-38px)", opacity: 0, offset: 0 },
        { transform: "translateY(0)", opacity: 1, offset: 0.55 },
        { transform: "translateY(-10px)", offset: 0.72 },
        { transform: "translateY(0)", offset: 0.86 },
        { transform: "translateY(-2.5px)", offset: 0.94 },
        { transform: "translateY(0)", offset: 1 },
      ],
      { duration: k.enterMs * 1.05, delay: k.enterMs * 1.15, easing: k.easeOut },
      bag
    )
  );

  return Promise.all(jobs).then(() => undefined);
}

/** Phase 1 of `app-switch`: the current letters leave, reverse stagger. */
export function playLettersOut(
  parts: BrandParts,
  bag: Animation[]
): Promise<void> {
  if (reducedMotion()) return Promise.resolve();
  const k = readKnobs(parts.host);
  const last = parts.letters.length - 1;
  const jobs = parts.letters.map((letter, i) =>
    run(
      letter,
      [{ fillOpacity: 1 }, { fillOpacity: 0 }],
      {
        duration: k.switchMs * 0.55,
        delay: (last - i) * k.staggerMs * 0.6,
        easing: k.easeIn,
        fill: "both", // hold 0 until the re-render replaces the letters
      },
      bag
    )
  );
  return Promise.all(jobs).then(() => undefined);
}

/**
 * Phase 2 of `app-switch`: the new letters arrive, the dot slides from the
 * old name's end to the new one (`dotTravelPx` in user units, may be 0), and
 * the bowl gives a small hinge-nod acknowledging the new name.
 */
export function playLettersIn(
  parts: BrandParts,
  dotTravelPx: number,
  bag: Animation[]
): Promise<void> {
  if (reducedMotion()) return Promise.resolve();
  const k = readKnobs(parts.host);
  const jobs: Promise<void>[] = [];

  parts.letters.forEach((letter, i) => {
    jobs.push(
      run(
        letter,
        [{ fillOpacity: 0 }, { fillOpacity: 1 }],
        {
          duration: k.switchMs * 0.7,
          delay: k.switchMs * 0.25 + i * k.staggerMs,
          easing: k.easeOut,
        },
        bag
      )
    );
  });

  if (dotTravelPx !== 0) {
    jobs.push(
      run(
        parts.dot,
        [
          { transform: `translateX(${-dotTravelPx}px)` },
          { transform: "translateX(0)" },
        ],
        { duration: k.switchMs * 1.15, easing: k.easeSwing },
        bag
      )
    );
  }

  jobs.push(
    run(
      parts.bowl,
      [
        { transform: "rotate(0deg)", offset: 0 },
        { transform: "rotate(-2.4deg)", offset: 0.4 },
        { transform: "rotate(0deg)", offset: 1 },
      ],
      { duration: k.switchMs * 1.2, easing: k.easeOut },
      bag
    )
  );

  return Promise.all(jobs).then(() => undefined);
}

/** The `dot-bounce` timeline — a playful ping on the full stop. */
export function playDotBounce(
  parts: BrandParts,
  bag: Animation[]
): Promise<void> {
  if (reducedMotion()) return Promise.resolve();
  const k = readKnobs(parts.host);
  return run(
    parts.dot,
    [
      { transform: "translateY(0) scale(1)", offset: 0 },
      { transform: "translateY(-12px) scale(1)", offset: 0.35 },
      { transform: "translateY(0) scale(1.12, 0.88)", offset: 0.62 },
      { transform: "translateY(-4px) scale(1)", offset: 0.8 },
      { transform: "translateY(0) scale(1)", offset: 1 },
    ],
    { duration: k.switchMs * 1.6, easing: "ease-in-out" },
    bag
  );
}

/** Cancel every animation in the bag (a newer timeline takes over). */
export function cancelAll(bag: Animation[]): void {
  for (const animation of bag.splice(0)) {
    try {
      animation.cancel();
    } catch {
      /* already done */
    }
  }
}
